import type { ReactNode } from 'react';
import { defaultShouldDehydrateQuery } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { AuthProvider } from '@/features/auth/AuthContext';
import { queryClient, queryPersister } from '@/app/queryClient';

interface ProvidersProps {
  children: ReactNode;
}

export function Providers({ children }: ProvidersProps) {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: queryPersister,
        maxAge: 1000 * 60 * 60 * 24,
        buster: 'v4-decom-fields',
        dehydrateOptions: {
          shouldDehydrateQuery: (query) => {
            if (!defaultShouldDehydrateQuery(query)) return false;
            // Graph data is large and cheap to refetch — never worth persisting.
            if (query.queryKey[0] === 'orbis-intelligence') return false;
            // Marketplace entitlement must always be live — a cached ACTIVE
            // entitlement would outlive a suspension/cancellation for 24h.
            if (query.queryKey[0] === 'marketplace-access') return false;
            // Home hooks resolve to demo values rather than throwing when an
            // endpoint is unreachable. Persisting that would cache a one-off
            // outage to disk for `maxAge` (24h) and show "Sample" chips on every
            // later load until a refetch lands — so drop those results instead.
            const data = query.state.data as { isFallback?: boolean } | undefined;
            if (query.queryKey[0] === 'home' && data?.isFallback === true) return false;
            return true;
          },
        },
      }}
    >
      <AuthProvider>{children}</AuthProvider>
    </PersistQueryClientProvider>
  );
}
